import { MaskedLines, FadeIn, Reveal } from "@/components/Motion";

const SECTIONS = [
  {
    title: "1. Overview",
    body: [
      "VeloPaths (\"VeloPaths,\" \"we,\" \"us,\" or \"our\") operates the VeloPaths marketing website (velopaths.com), the VeloPaths mobile application for iOS and Android (the \"Rider app\"), and the VeloPaths partner portals. This Privacy Policy explains what information we collect, how we use it, and the choices you have.",
      "By using our website, mobile app, or portals, you agree to the practices described in this policy.",
    ],
  },
  {
    title: "2. Information We Collect",
    list: [
      "Email addresses — when you join the launch waitlist, subscribe to updates, or contact us.",
      "Contact form information — your name, email address, organization, and message when you submit an enquiry.",
      "Partner and advertiser information — business name, contact details, and billing-related information when you apply to advertise or lease zones.",
      "Location data — the Rider app uses your device's location to show your position on routes, surface nearby curated stops, and power detour suggestions (see Section 4).",
      "Usage and analytics data — pages visited, features used, device type, and approximate region (see Section 5).",
      "Cookie data — small identifiers stored on your device (see Section 6).",
    ],
  },
  {
    title: "3. How We Use Your Information",
    list: [
      "To operate the website, app, and partner portals.",
      "To send launch notifications and waitlist updates you asked for.",
      "To respond to enquiries, partnership applications, and area requests.",
      "To display routes, curated stops, and detours relevant to your location in the Rider app.",
      "To improve our routes, curation, and product experience.",
      "To meet legal obligations and protect against fraud or misuse.",
    ],
    body: ["We do not sell your personal information to anyone, ever."],
  },
  {
    title: "4. Location Data in the Rider App",
    body: [
      "The VeloPaths Rider app requests access to your device's location to provide its core functionality: showing where you are on a bike path, displaying nearby approved stops, and routing custom detours.",
      "Location is used in real time while you use the app. We do not track your location in the background when the app is closed, and we do not build or sell location histories. Approximate location may be used in aggregate to understand which routes and regions are popular.",
      "You can deny or revoke location permission at any time in your device settings. The app will still open, but route guidance and nearby-stop features will be limited.",
    ],
  },
  {
    title: "5. Analytics",
    body: [
      "We use privacy-conscious analytics to understand how visitors use the website and app — for example, which pages are visited and which features are used. Analytics data is aggregated and is not used to identify individual riders.",
      "We use this information solely to improve VeloPaths. We do not use third-party advertising trackers on our website.",
    ],
  },
  {
    title: "6. Cookies",
    body: [
      "Our website uses a small number of cookies: essential cookies required for the site to function, and preference cookies that remember choices you make (such as staying signed in on the partner portal).",
      "Most browsers let you refuse or delete cookies through their settings. Blocking essential cookies may affect how the site works.",
    ],
  },
  {
    title: "7. How We Share Information",
    body: [
      "We share information only with service providers that help us operate VeloPaths — for example, our email delivery provider (which delivers waitlist and enquiry notifications) and our hosting providers. These providers may process information only on our behalf and under confidentiality obligations.",
      "We may also disclose information if required by law, or to protect the rights, safety, and security of VeloPaths, our users, or the public.",
      "If VeloPaths is involved in a merger, acquisition, or sale of assets, user information may be transferred as part of that transaction, subject to this policy.",
    ],
  },
  {
    title: "8. Data Retention",
    body: [
      "We keep waitlist and contact information for as long as needed to operate the service and communicate with you, or until you ask us to delete it. Partner and advertiser records are kept for the duration of the business relationship and as required for accounting and legal purposes.",
    ],
  },
  {
    title: "9. Your Rights and Choices",
    list: [
      "Unsubscribe from emails at any time using the link in any message we send.",
      "Request a copy, correction, or deletion of your personal information by emailing info@velopaths.com.",
      "Control location access through your device settings.",
      "Control cookies through your browser settings.",
    ],
    body: [
      "Depending on where you live, you may have additional rights under laws such as the GDPR or CCPA/CPRA, including the right to know what data we hold and the right to opt out of any sale of personal information. Because we never sell personal information, there is nothing to opt out of — but we honor all applicable rights requests sent to info@velopaths.com.",
    ],
  },
  {
    title: "10. Data Security",
    body: [
      "We use industry-standard safeguards including encrypted connections (HTTPS), access controls, and credential protection to keep your information safe. No method of transmission or storage is 100% secure, but we work to protect your information and limit access to those who need it to operate the service.",
    ],
  },
  {
    title: "11. Children's Privacy",
    body: [
      "VeloPaths is not directed at children under 13, and we do not knowingly collect personal information from children under 13. If you believe a child has provided us personal information, contact us at info@velopaths.com and we will delete it.",
    ],
  },
  {
    title: "12. Changes to This Policy",
    body: [
      "We may update this Privacy Policy as the platform grows. If we make material changes, we will update the date below and, where appropriate, notify you by email or in the app. The current version is always available at velopaths.com/privacy.",
    ],
  },
  {
    title: "13. Contact Us",
    body: [
      "Questions about this policy or your information? Email us at info@velopaths.com. VeloPaths is based in Newport, Rhode Island, USA.",
    ],
    mail: true,
  },
];

export default function Privacy() {
  return (
    <main data-testid="page-privacy">
      <section className="relative overflow-hidden bg-[#0B2B22] pt-40 pb-20 sm:pb-24">
        <div className="grain absolute inset-0" />
        <div className="absolute -right-24 -top-24 size-72 rounded-full bg-[#2D6A4F]/40 blur-3xl" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
          <FadeIn delay={0.05}>
            <p className="font-mono text-xs sm:text-sm font-bold uppercase tracking-[0.3em] text-[#7FB998]">
              Legal
            </p>
          </FadeIn>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-[#F4F7F4]">
            <MaskedLines lines={["Privacy Policy."]} />
          </h1>
          <FadeIn delay={0.55}>
            <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[#F4F7F4]/75">
              How VeloPaths collects, uses, and protects your information — across the website, the
              Rider app, and the partner portals.
            </p>
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-[#7FB998]">
              Last updated: September 23, 2026
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-20 sm:px-10 sm:py-24 lg:px-16">
        <div data-testid="privacy-sections" className="space-y-12">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.title} delay={Math.min(i * 0.04, 0.3)}>
              <div className="border-t border-[#154234]/20 pt-8">
                <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-[#0B2B22]">
                  {s.title}
                </h2>
                {s.body?.map((p, j) => (
                  <p key={j} className="mt-4 text-sm sm:text-base leading-relaxed text-[#52635B]">
                    {p}
                  </p>
                ))}
                {s.list && (
                  <ul className="mt-4 space-y-2.5">
                    {s.list.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm sm:text-base leading-relaxed text-[#52635B]">
                        <span className="mt-2 size-1.5 shrink-0 rounded-full bg-[#E05A47]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
                {s.mail && (
                  <a
                    href="mailto:info@velopaths.com?subject=Privacy%20question"
                    data-testid="btn-privacy-contact"
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#0B2B22] px-6 py-3 text-sm font-bold text-[#F4F7F4] transition-colors hover:bg-[#154234]"
                  >
                    Email info@velopaths.com
                  </a>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
